<template>
  <div class="preview-status" :class="`preview-status--${state}`">
    <div class="preview-status__head">
      <span class="preview-status__icon" aria-hidden="true">
        <q-spinner v-if="state === 'running'" size="14px" />
        <q-icon v-else-if="state === 'completed'" name="check_circle" size="16px" />
        <q-icon v-else-if="state === 'failed'" name="error_outline" size="16px" />
        <q-icon v-else name="hourglass_empty" size="16px" />
      </span>
      <span class="preview-status__title">{{ title }}</span>
    </div>

    <p v-if="errorMessage" class="preview-status__error">{{ errorMessage }}</p>

    <template v-if="state === 'completed' && summary">
      <p
        v-if="proposalSummary"
        class="preview-status__headline"
        v-html="renderInlineMarkdown(proposalSummary)"
      />

      <p class="preview-status__change-count">
        <template v-if="summary.changedCount === 0">
          No fields would change. The document would look the same.
        </template>
        <template v-else-if="summary.changedCount === 1">
          1 field would change.
        </template>
        <template v-else>
          {{ summary.changedCount }} fields would change.
        </template>
      </p>

      <section
        v-if="changedRows.length || linesChangeSummary || objectArrayChangeSummaries.length"
        class="preview-status__section preview-status__section--changes"
      >
        <h4>Changes</h4>
        <ul>
          <li v-for="row in changedRows" :key="row.field">
            <span class="preview-status__diff-label">{{ row.label }}</span>
            <span class="preview-status__diff-before">{{ row.was }}</span>
            <q-icon name="east" size="12px" class="preview-status__diff-arrow" />
            <span class="preview-status__diff-after">{{ row.value }}</span>
          </li>
          <li v-if="linesChangeSummary" class="preview-status__diff-lines-row">
            <span class="preview-status__diff-label">Line items</span>
            <span class="preview-status__diff-after">{{ linesChangeSummary }}</span>
          </li>
          <li
            v-for="entry in objectArrayChangeSummaries"
            :key="entry.field"
            class="preview-status__diff-lines-row"
          >
            <span class="preview-status__diff-label">{{ entry.label }}</span>
            <span class="preview-status__diff-after">{{ entry.summary }}</span>
          </li>
        </ul>
      </section>

      <details class="preview-status__more">
        <summary>Show full preview</summary>

        <p class="preview-status__diff-note">
          Compared against the original extraction with all current rules and this proposed rule applied — not just the effect of this rule on its own.
        </p>

        <section
          v-if="summary.scalarRows.length"
          class="preview-status__section"
        >
          <h4>Document</h4>
          <dl>
            <div
              v-for="row in summary.scalarRows"
              :key="row.field"
              :class="{ 'preview-status__row--changed': row.was !== undefined }"
            >
              <dt>{{ row.label }}</dt>
              <dd>
                <span class="preview-status__value">{{ row.value }}</span>
                <span v-if="row.was !== undefined" class="preview-status__was">
                  was {{ row.was }}
                </span>
              </dd>
            </div>
          </dl>
        </section>

        <section
          v-if="summary.lines"
          class="preview-status__section preview-status__section--lines"
          :class="{ 'preview-status__section--lines-changed': summary.lines.changed }"
        >
          <h4>Line items</h4>
          <div class="preview-status__lines-tables">
            <div class="preview-status__lines-table">
              <div class="preview-status__lines-caption">After</div>
              <LineTable
                :columns="summary.lines.columns"
                :rows="summary.lines.after"
                side="after"
              />
            </div>
            <div
              v-if="summary.lines.changed"
              class="preview-status__lines-table preview-status__lines-table--before"
            >
              <div class="preview-status__lines-caption">Before</div>
              <LineTable
                :columns="summary.lines.columns"
                :rows="summary.lines.before"
                side="before"
              />
            </div>
          </div>
        </section>

        <section
          v-for="entry in summary.objectArrays"
          :key="entry.field"
          class="preview-status__section preview-status__section--lines"
          :class="{ 'preview-status__section--lines-changed': entry.changed }"
        >
          <h4>{{ entry.label }}</h4>
          <div class="preview-status__lines-tables">
            <div class="preview-status__lines-table">
              <div class="preview-status__lines-caption">After</div>
              <LineTable
                :columns="entry.columns"
                :rows="entry.after"
                side="after"
              />
            </div>
            <div
              v-if="entry.changed"
              class="preview-status__lines-table preview-status__lines-table--before"
            >
              <div class="preview-status__lines-caption">Before</div>
              <LineTable
                :columns="entry.columns"
                :rows="entry.before"
                side="before"
              />
            </div>
          </div>
        </section>
      </details>

      <details v-if="resultInlineJson" class="preview-status__raw">
        <summary>Show technical details</summary>
        <pre>{{ formattedJson }}</pre>
      </details>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue';
import { renderInlineMarkdown } from '../formatting';
import { LIVE_EXTRACTION_KEY } from '../injection';
import { parsePreviewResult, previewSummary } from '../previewDiff';
import LineTable from './PreviewLineTable.vue';

const props = defineProps<{
  previewRunId: string;
  status?: 'Queued' | 'Running' | 'Completed' | 'Failed';
  errorMessage?: string;
  resultInlineJson?: string;
  proposalSummary?: string;
}>();

const liveExtraction = inject(LIVE_EXTRACTION_KEY, ref(null));

const state = computed<'queued' | 'running' | 'completed' | 'failed'>(() => {
  switch (props.status) {
    case 'Completed': return 'completed';
    case 'Failed': return 'failed';
    case 'Running': return 'running';
    default: return 'queued';
  }
});

const title = computed(() => {
  switch (state.value) {
    case 'completed': return 'Preview ready';
    case 'failed': return 'Preview failed';
    case 'running': return 'Running preview…';
    default: return 'Preview queued';
  }
});

const summary = computed(() => {
  if (state.value !== 'completed') return null;
  const proposed = parsePreviewResult(props.resultInlineJson);
  return previewSummary(proposed, liveExtraction.value);
});

const changedRows = computed(() => summary.value?.scalarChanges ?? []);

// Single-line description of the line-item delta, shown in the always-visible Changes list
// when line items changed. Pairs with the full before/after table inside the expander.
const linesChangeSummary = computed(() => {
  const lines = summary.value?.lines;
  if (!lines?.changed) return null;
  const before = lines.before.filter((r) => !r.ghost).length;
  const after = lines.after.filter((r) => !r.ghost).length;
  if (before !== after) return `${before} → ${after}`;
  const changedRowCount = lines.after.filter((r) => r.cells.some((c) => c.was !== undefined)).length;
  if (changedRowCount === 0) return null;
  return changedRowCount === 1 ? '1 line item changed' : `${changedRowCount} line items changed`;
});

// One headline per changed object-array (e.g. allowanceCharges). Mirrors linesChangeSummary
// so the always-visible Changes list stays consistent across all table-shaped diffs.
const objectArrayChangeSummaries = computed(() => {
  const arrays = summary.value?.objectArrays ?? [];
  return arrays
    .filter((arr) => arr.changed)
    .map((arr) => {
      const summaryText =
        arr.rowCount !== arr.rowCountWas
          ? `${arr.rowCountWas} → ${arr.rowCount}`
          : (() => {
              const changedRowCount = arr.after.filter((r) =>
                r.cells.some((c) => c.was !== undefined)
              ).length;
              if (changedRowCount === 0) return 'changed';
              return changedRowCount === 1
                ? '1 entry changed'
                : `${changedRowCount} entries changed`;
            })();
      return { field: arr.field, label: arr.label, summary: summaryText };
    });
});

const formattedJson = computed(() => {
  if (!props.resultInlineJson) return '';
  try {
    return JSON.stringify(JSON.parse(props.resultInlineJson), null, 2);
  } catch {
    return props.resultInlineJson;
  }
});
</script>

<style scoped lang="scss">
.preview-status {
  margin: 6px 0;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  font-size: 13px;

  &--running { border-color: #c7d2fe; background: #eef2ff; }
  &--completed { border-color: #bbf7d0; background: #f0fdf4; }
  &--failed { border-color: #fecaca; background: #fef2f2; }

  &__head {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    color: #111827;
  }

  &__icon { display: inline-flex; color: inherit; }

  &__error {
    margin: 6px 0 0;
    color: #b91c1c;
    font-size: 12px;
  }

  &__headline {
    margin: 8px 0 0;
    font-size: 13px;
    color: #1f2937;
    line-height: 1.45;

    :deep(code) {
      font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
      font-size: 0.92em;
      padding: 1px 5px;
      background: #ecfdf5;
      color: #065f46;
      border-radius: 4px;
      border: 1px solid #a7f3d0;
    }
  }

  &__change-count {
    margin: 8px 0 0;
    font-size: 12px;
    font-weight: 600;
    color: #065f46;
  }

  &__diff-note {
    margin: 4px 0 0;
    font-size: 11.5px;
    color: #6b7280;
    line-height: 1.4;
    font-style: italic;
  }

  &__section {
    margin-top: 10px;
    padding: 8px 10px;
    background: #ffffff;
    border: 1px solid #d1fae5;
    border-radius: 8px;

    h4 {
      margin: 0 0 6px;
      font-size: 11.5px;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: #6b7280;
    }

    dl {
      margin: 0;
      display: grid;
      grid-template-columns: minmax(110px, 38%) 1fr;
      row-gap: 4px;
      column-gap: 12px;
      font-variant-numeric: tabular-nums;
    }

    dl > div {
      display: contents;
    }

    dt {
      color: #4b5563;
    }

    dd {
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 1px;
      color: #111827;
      word-break: break-word;
    }
  }

  &__row--changed {
    dd .preview-status__value {
      color: #065f46;
      font-weight: 600;
    }
  }

  &__section--changes {
    border-color: #6ee7b7;
    background: #ecfdf5;

    ul {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    li {
      display: grid;
      grid-template-columns: minmax(110px, 38%) 1fr auto 1fr;
      align-items: baseline;
      gap: 8px;
      font-variant-numeric: tabular-nums;
      font-size: 12.5px;
    }
  }

  &__diff-label { color: #4b5563; font-weight: 500; }
  &__diff-before {
    color: #b91c1c;
    text-decoration: line-through;
    text-decoration-color: rgba(185, 28, 28, 0.45);
    word-break: break-word;
  }
  &__diff-arrow { color: #6b7280; }
  &__diff-after {
    color: #065f46;
    font-weight: 600;
    word-break: break-word;
  }

  &__was {
    color: #b91c1c;
    text-decoration: line-through;
    text-decoration-color: rgba(185, 28, 28, 0.45);
    font-size: 11.5px;
  }

  &__value--changed {
    color: #065f46;
    font-weight: 600;
  }

  &__diff-lines-row {
    // Single-column row in the Changes list — no before/after grid since the actual line
    // table lives inside the expander; here we just summarise.
    grid-template-columns: minmax(110px, 38%) 1fr !important;
  }

  &__section--lines {
    padding: 8px 10px;
  }

  &__section--lines-changed {
    border-color: #6ee7b7;
    background: #ecfdf5;
  }

  &__lines-tables {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__lines-table {
    border: 1px solid #d1fae5;
    border-radius: 6px;
    background: #ffffff;
    overflow: hidden;

    &--before {
      border-color: #fecaca;
      background: #fff5f5;
    }
  }

  &__lines-caption {
    padding: 4px 8px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6b7280;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
  }

  &__more {
    margin-top: 10px;
    font-size: 12.5px;

    summary {
      cursor: pointer;
      color: #6b7280;
      user-select: none;
      padding: 2px 0;
      font-weight: 500;

      &:hover { color: #1f2937; }
    }

    > .preview-status__diff-note { margin-top: 6px; }
  }

  &__raw {
    margin-top: 10px;
    font-size: 11.5px;

    summary {
      cursor: pointer;
      color: #6b7280;
      user-select: none;
      padding: 2px 0;

      &:hover { color: #1f2937; }
    }

    pre {
      margin: 6px 0 0;
      padding: 8px 10px;
      background: #0f172a;
      color: #e2e8f0;
      border-radius: 8px;
      line-height: 1.45;
      overflow-x: auto;
      max-height: 220px;
    }
  }
}

// At narrow widths the 4-column changes grid (label | before | arrow | after)
// and 2-column section dl can't honour their minmax(110 px) tracks. Flatten to
// one column so each row stacks legibly instead of overflowing the bubble.
@container ra (max-width: 479px) {
  .preview-status { padding: 10px 12px; }
  .preview-status__section { padding: 8px; }
  .preview-status__section dl {
    grid-template-columns: 1fr;
    row-gap: 6px;
  }
  .preview-status__section dl > div {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }
  .preview-status__section--changes li {
    grid-template-columns: 1fr;
    gap: 2px;
    padding: 4px 0;
    border-bottom: 1px dashed #bbf7d0;
    &:last-child { border-bottom: none; }
  }
  .preview-status__diff-arrow { display: none; }
  .preview-status__diff-before::before { content: 'was '; opacity: 0.7; }
  .preview-status__diff-after::before { content: '→ '; color: #6b7280; }
}

@container ra (max-width: 359px) {
  .preview-status { padding: 9px 10px; font-size: 12.5px; }
  .preview-status__head { font-size: 13px; }
  .preview-status__raw pre { font-size: 11px; padding: 6px 8px; }
}
</style>
